"use client";

import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import { createPortal } from "react-dom";
import { usePortalTranslations } from "../i18n/client";
import { AccountSettingsDialog } from "./PortalAccountSettings";
import { usePortalPreferences } from "./PortalPreferences";
import styles from "./PortalProfileMenu.module.css";

type PortalProfileMenuProps = {
  identity: { name: string; email: string };
  accessHref?: string;
  loggingOut: boolean;
  onLogout: () => void | Promise<void>;
  onTopbar?: boolean;
};

const appearances = [
  { value: "light", label: "profile.light" },
  { value: "dark", label: "profile.dark" },
  { value: "system", label: "profile.system" },
] as const;

function userInitials(name: string, email: string) {
  const words = (name.trim() || email.split("@")[0] || "U").replace(/[._-]+/g, " ").split(/\s+/).filter(Boolean);
  return (words.length > 1 ? `${words[0][0]}${words[words.length - 1][0]}` : words[0].slice(0, 2)).toLocaleUpperCase();
}

function UserMark({ initials, large = false }: { initials: string; large?: boolean }) {
  return <span className={`${styles.avatar} ${large ? styles.avatarLarge : ""}`} aria-hidden="true"><span>{initials}</span></span>;
}

function AppearanceIcon({ appearance }: { appearance: typeof appearances[number]["value"] }) {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {appearance === "light" ? <><circle cx="12" cy="12" r="4" /><path d="M12 2v2m0 16v2M2 12h2m16 0h2M5 5l1.5 1.5m11 11L19 19M5 19l1.5-1.5m11-11L19 5" /></> : appearance === "dark" ? <path d="M20.9 13a9 9 0 0 1-9.9-9.9A9 9 0 1 0 20.9 13Z" /> : <><rect x="3" y="4" width="18" height="13" rx="2" /><path d="M8 21h8m-4-4v4" /></>}
  </svg>;
}

export function PortalProfileMenu({ identity, accessHref, loggingOut, onLogout, onTopbar = false }: PortalProfileMenuProps) {
  const { theme, setTheme, locale, setLocale } = usePortalPreferences();
  const t = usePortalTranslations();
  const [open, setOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const initials = userInitials(identity.name, identity.email);

  useEffect(() => {
    if (!open) return;

    panel.current?.querySelector<HTMLElement>("button, a[href]")?.focus({ preventScroll: true });

    function closeOutside(event: PointerEvent) {
      if (event.target instanceof Node && !container.current?.contains(event.target)) setOpen(false);
    }
    function closeAfterFocusLeaves(event: FocusEvent) {
      if (event.target instanceof Node && !container.current?.contains(event.target)) setOpen(false);
    }
    document.addEventListener("pointerdown", closeOutside);
    document.addEventListener("focusin", closeAfterFocusLeaves);
    return () => {
      document.removeEventListener("pointerdown", closeOutside);
      document.removeEventListener("focusin", closeAfterFocusLeaves);
    };
  }, [open]);

  function close(returnFocus = false) {
    setOpen(false);
    if (returnFocus) trigger.current?.focus({ preventScroll: true });
  }

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === "Escape" && open) {
      event.preventDefault();
      event.stopPropagation();
      close(true);
    }
    if (event.target === trigger.current && event.key === "ArrowDown") {
      event.preventDefault();
      setOpen(true);
    }
  }

  return <>
    <div className={`${styles.root} ${onTopbar ? styles.onTopbar : ""}`} ref={container} onKeyDown={handleKeyDown}>
      <button
        ref={trigger}
        type="button"
        className={styles.trigger}
        aria-label={t("profile.trigger")}
        aria-haspopup="dialog"
        aria-controls={open ? menuId : undefined}
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <UserMark initials={initials} />
        <svg className={styles.chevron} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
      </button>
      {open ? <div ref={panel} className={styles.panel} id={menuId} role="dialog" aria-label={t("profile.account")}>
        <div className={styles.identity}>
          <UserMark initials={initials} large />
          <div><strong>{identity.name || t("profile.myAccount")}</strong><span>{identity.email}</span></div>
        </div>
        <button type="button" className={styles.action} onClick={() => { close(true); setSettingsOpen(true); }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="8" r="4" /><path d="M4 21v-2a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v2" /></svg>
          {t("profile.settings")}
        </button>
        {accessHref ? <a className={styles.action} href={accessHref} onClick={() => close()}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m9 18 6-6-6-6M4 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H4" /></svg>
          {t("profile.enterPortal")}
        </a> : null}
        <fieldset className={styles.appearance}>
          <legend>{t("profile.appearance")}</legend>
          <div className={styles.appearanceOptions}>
            {appearances.map((appearance) => <label key={appearance.value} className={styles.appearanceChoice}>
              <input type="radio" name={`${menuId}-appearance`} value={appearance.value} checked={theme === appearance.value} onChange={() => setTheme(appearance.value)} />
              <span><AppearanceIcon appearance={appearance.value} />{t(appearance.label)}</span>
            </label>)}
          </div>
        </fieldset>
        <label className={styles.language}>
          <span id={`${menuId}-language`}>{t("profile.language")}</span>
          <select aria-labelledby={`${menuId}-language`} value={locale} onChange={(event) => setLocale(event.target.value === "en" ? "en" : "cs")}>
            <option value="cs" lang="cs">Čeština</option>
            <option value="en" lang="en">English</option>
          </select>
        </label>
        <button type="button" className={`${styles.action} ${styles.logout}`} disabled={loggingOut} onClick={() => { close(true); void onLogout(); }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M14 8V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h7a2 2 0 0 0 2-2v-3M10 12h11m-4-4 4 4-4 4" /></svg>
          {t(loggingOut ? "profile.loggingOut" : "profile.logout")}
        </button>
      </div> : null}
    </div>
    {settingsOpen ? createPortal(<AccountSettingsDialog identity={identity} onClose={() => setSettingsOpen(false)} />, document.body) : null}
  </>;
}
